import React, { useState, FC } from 'react'
import { Loader2Icon } from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import { v4 as uuidv4 } from 'uuid'

import { Button } from '../../ui/button'

import FirestoreService from '../../../services/firestoreService'
import { httpService } from '../../../services/httpService'
import useToasterHelper from '../../../helpers/ToasterHelper'
import { getUserFullName } from '../../../helpers/helpers'

import { IUserInterface } from '../../../models/users/user.interface'

import { MESSAGES_CONST } from '../../../const/messages-const'
import { CONST } from '../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface SetupFormProps {
  user: IUserInterface
  organizer: IUserInterface
  userStripeAccountId: string
  clientSecret: string
  closeModal: () => void
  onSave?: (customerId: string) => Promise<void>
  isApproved: boolean
}
const SetupForm: FC<SetupFormProps> = ({
  clientSecret,
  user,
  organizer,
  userStripeAccountId,
  closeModal,
  onSave,
  isApproved,
}) => {
  const toasterFunctions = useToasterHelper()

  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    try {
      setLoading(true)

      const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string, {
        stripeAccount: userStripeAccountId || '',
      })

      const stripe = await stripePromise
      if (!stripe) {
        toasterFunctions.error({ message: 'Stripe failed to load.' })
        setLoading(false)
        return
      }

      const { error: collectError, setupIntent: collectSetupIntent } =
        await stripe.collectBankAccountForSetup({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: getUserFullName(user),
                email: user.userEmail,
              },
            },
          },
        })

      if (collectError) {
        return toasterFunctions.error({
          message: collectError?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      }

      const { error: confirmError, setupIntent: confirmSetupIntent } =
        await stripe.confirmUsBankAccountSetup(collectSetupIntent?.client_secret || '')

      if (confirmError) {
        return toasterFunctions.error({
          message: confirmError?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      }

      await httpService({
        url: 'default_payment_method',
        method: 'POST',
        data: {
          pmId: confirmSetupIntent?.payment_method,
          userStripeAccountId: userStripeAccountId,
          userEmail: user.userEmail,
        },
      })

      if (isApproved && confirmSetupIntent?.payment_method) {
        await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, user?.id, {
          paymentMethodsAccess: {
            ...user.paymentMethodsAccess,
            [confirmSetupIntent.payment_method as string]: [
              {
                requesterId: organizer?.id,
                status: 'approved',
                cardNumber: null,
                token: uuidv4(),
                created: new Date().toString(),
              },
            ],
          },
        })
      }

      if (onSave) await onSave(user.userStripeId || '')

      toasterFunctions.success({ message: 'Bank account added successfully' })
      closeModal()
    } catch (e) {
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className="w-full">
      <Button
        className="w-full text-base font-normal py-3.5 rounded-[10px]"
        variant="red"
        disabled={loading}
        onClick={handleSubmit}
      >
        {loading && <Loader2Icon className="animate-spin w-6" />}
        Connect Bank Account
      </Button>
    </form>
  )
}

interface BankAccountSetupProps {
  user: IUserInterface
  organizer: IUserInterface
  userStripeAccountId: string
  clientSecret: string
  closeModal: () => void
  onSave?: (customerId: string) => Promise<void>
  isApproved: boolean
}

export const BankAccountSetup: FC<BankAccountSetupProps> = ({
  user,
  organizer,
  userStripeAccountId,
  clientSecret,
  closeModal,
  onSave,
  isApproved,
}) => {
  if (!clientSecret || !userStripeAccountId) {
    return (
      <Button
        disabled={true}
        variant="red"
        className="w-full text-base font-normal py-3.5 rounded-[10px]"
      >
        <Loader2Icon className="animate-spin w-6" />
        Connect Bank Account
      </Button>
    )
  }

  return (
    <SetupForm
      user={user}
      organizer={organizer}
      userStripeAccountId={userStripeAccountId}
      clientSecret={clientSecret}
      closeModal={closeModal}
      onSave={onSave}
      isApproved={isApproved}
    />
  )
}
